/**
 * Our Mapbox access token is globally available at `process.env.MAPBOX_ACCESS_TOKEN` but
 * we do allow for a custom access token to be passed in to a `Map` or `Geocoder`
 * component if the consuming application has its own token.
 *
 *  @param {string} token
 * @returns {string|null} access token
 */
export const getAccessToken = (token) => {
  if (token) {
    return token;
  }

  // try catch here prevents "ReferenceError: process is not defined" which can happen
  // when webpack or tooling that sets process.env values is not configured correctly or
  // if MAPBOX_ACCESS_TOKEN is missing
  try {
    return process.env.MAPBOX_ACCESS_TOKEN;
  } catch {
    // ignore
  }

  return null;
};

/**
 * Map Strava specific local to Mapbox expected local
 * @param {string} lang Strava locale string
 * @returns {string} MapboxLocal expected locale
 */
export const languageMapper = (lang) => {
  const regex = new RegExp('(.*)-');
  const shortLang = regex.exec(lang);

  switch (lang) {
    case 'zh':
    case 'zh-CN':
      return 'zh-Hans';
    case 'zh-TW':
      return 'zh-Hant-TW';
    default:
      if (shortLang) {
        return shortLang[1];
      }
      return lang;
  }
};

/**
 * Convert list of [lat, lng] to [lng, lat]
 * @param {Array} latLng list containing [lat, lng]
 */
export const latLngToLngLat = (latLng) => {
  if (!latLng || latLng.length === 0) {
    return [];
  }

  if (Array.isArray(latLng[0])) {
    return latLng.map((p) => [p[1], p[0]]);
  }
  return [latLng[1], latLng[0]];
};

/**
 * Get if a givev point is in the bounds of the map
 * @param {Object} point Object containing lat and lng coordinates
 * @param {Object} bounds map.getBounds() object
 */
export const inBounds = ({ lng, lat }, { _ne, _sw }) => {
  const lg = (lng - _ne.lng) * (lng - _sw.lng) < 0;
  const lt = (lat - _ne.lat) * (lat - _sw.lat) < 0;
  return lg && lt;
};

/**
 * Returns coordinates for polygon circle
 *
 * @param {Array} center Array containing [lat, lng]
 * @param {*} radius Radius size in kilometers
 * @param {*} points How many points to use to generate a circle
 *
 * @returns {Array} Array of [lat, lng] coordinates for a circle
 */
export const getCircleCoords = (center, radius, points = 32) => {
  const centerCoords = {
    lat: center[1],
    lng: center[0]
  };
  const distanceX =
    radius / (111.32 * Math.cos((centerCoords.lat * Math.PI) / 180));
  const distanceY = radius / 110.574;
  const coords = [];

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < points; i++) {
    const theta = (i / points) * (2 * Math.PI);
    const x = distanceX * Math.cos(theta);
    const y = distanceY * Math.sin(theta);

    coords.push([centerCoords.lng + x, centerCoords.lat + y]);
  }
  coords.push(coords[0]);

  return coords;
};

/**
 * Returns id if layer exists on the map and null when it does not, this prevents
 * mapbox-gl throwing an error when layer does not exist and not drawing anything on the
 * map
 * @param {Instance} map mapbox-gl instance
 * @param {String} id Layer id
 *
 * @returns {String|null} id when layer exists and null if layer does not exist
 */
export const getBeforeIdToUse = (map, id) => (map.getLayer(id) ? id : null);

/**
 * When switching route types in the route builder UI, some layers (trail networks, POIs) will not
 * update automatically unless the user interacts with the map (changes zoom level, clicks and
 * drags, etc). By clearing the tiles associated with those layers and reloading the layers the map
 * will update without any user interaction. This also allows us to append any query params we may
 * want to exist on tile requests.
 * @param map mapbox instance
 * @param {string} source Source id
 * @param {string} params Params that will be added to the tile URL at the end
 */
export const reloadLayerSource = (map, source, params) => {
  let mapSource = map.getSource(source);

  // If for some reason the requested source isn't available, return early before reloading to avoid
  // breaking the map
  if (!mapSource) {
    return;
  }

  const onStyleData = () => {
    mapSource = map.getSource(source);
    const { tiles } = mapSource;

    // When map style changes happen tiles can be undefined so we wait until tile are ready
    if (tiles) {
      map.off('styledata', onStyleData);

      // Get the base tile URL and append the given query params
      //
      // TODO: Potentially look into using URLSearchParams here so that when tile URL has
      // other params we don't conflict or override them unintentionally
      const updatedTiles = tiles.map((tile) => {
        const [baseUrl] = tile.split('?');
        return `${baseUrl}${params}`;
      });

      // Ideally we would want to use `setTiles` here as it is the native API to update
      // tiles, however on local development Nextjs's Fast Refresh does not work properly
      // and throws an error, we currently do not know the detail why and it might be
      // resolved in more modern Nextjs versions.
      //
      // So for now we are using internal API to update the tiles and force the map to
      // reload the tiles.
      //
      // mapSource.setTiles(updatedTiles);

      // Update the tiles URL
      /* eslint-disable-next-line no-param-reassign */
      map.getSource(source).tiles = updatedTiles;

      // Clear and update tile caches
      // https://github.com/mapbox/mapbox-gl-js/issues/2941
      /* eslint-disable */
      map.style._sourceCaches[`other:${source}`].clearTiles();
      map.style._sourceCaches[`other:${source}`].update(map.transform);
      map.style._sourceCaches[`symbol:${source}`].clearTiles();
      map.style._sourceCaches[`symbol:${source}`].update(map.transform);
      /* eslint-enable */
    }
  };

  // When map style changes happen tiles can be undefined so we wait until tiles exist,
  // however when tiles are already available we can immediately update them.
  if (mapSource?.tiles) {
    onStyleData();
  } else {
    map.on('styledata', onStyleData);
  }
};

/**
 * GraphQL returns route type as a string, and route_type param that needs to be sent to
 * /tiles/pois endpoint needs to be a thrift number
 * @param {string} routeType
 * @returns {number}
 */
export const getThriftRouteTypeFromGraphQlRouteType = (routeType) => {
  switch (routeType) {
    case 'Ride':
      return 1;
    case 'Run':
      return 2;
    case 'Walk':
      return 3;
    case 'Hike':
      return 4;
    case 'TrailRun':
      return 5;
    case 'GravelRide':
      return 6;
    case 'MountainBikeRide':
      return 7;
    default:
      // eslint-disable-next-line no-console
      console.warn(
        `Route type "${routeType}" is not supported as POI on the map`
      );
      return null;
  }
};

export default {
  getAccessToken,
  languageMapper,
  latLngToLngLat,
  inBounds,
  getCircleCoords,
  getBeforeIdToUse,
  reloadLayerSource,
  getThriftRouteTypeFromGraphQlRouteType
};
